import styles from './styles.scss';
import { rm, NO_ELEMENT, scrollToTarget } from 'utils';
import { useEffect, useReducer, useRef } from 'react';
import { useToggle } from 'hooks';
import {
  postSendPriceMatchQuestion,
  postSendExistingOrderPriceMatchQuestion
} from 'services/ApiCustomerSupportFormService';
import { Input, TextArea } from 'components/Shared/Form';
import { Button, LoaderButton } from 'components/Button';
import { IBasicResponse } from 'models/IBasicResponse';
import {
  BlueSectionContainer,
  ErrorContainer,
  SubmitContainer
} from 'components/CustomerSupportForms/Components/SectionContainer/SectionContainer';
import { useDispatch } from 'react-redux';
import { GtmCustomerSupportFormSubmit } from 'store/googleTagManager/actions';
import { FormType } from 'models/FormType';
import { isValid, priceMatchInitialState, reducer, productChanged } from './PriceMatchHelper';
import InputWithRules from 'components/Input/Input';
import {
  contactOptionChanged,
  emailRule,
  nameRule,
  phoneRule,
  OrderFormProps,
  addressChanged,
  addressLookupValidationError
} from '../Utils';
import { CustomerContact } from '../Components/CustomerContact/CustomerContact';
import ExtendedInfo from 'views/CustomerPage/OrderHistory/ExtendedInfo';
import { DisplayMode } from 'views/CustomerPage/OrderHistory/OrderLine';
import { CustomerPageGroupType } from 'models/CustomerPageGroupType';
import { Address } from 'components/CustomerSupportForms/Components/Address/Address';
import Spinner from 'components/Shared/Loader';

const QUESTION_MAX_LENGTH = 1000;

export const PriceMatchForm = ({ order, error, returnUrl, onSuccess }: OrderFormProps) => {
  const anchorRef = useRef(NO_ELEMENT);
  const scrollToTopIfNeeded = scrollToTarget(anchorRef);
  const [internalState, internalDispatch] = useReducer(reducer, priceMatchInitialState);
  const [loading, toggleLoading] = useToggle(false);
  const [addressChange, toggleAddressChange] = useToggle(false);
  const reduxDispatch = useDispatch();

  useEffect(() => {
    if (order) {
      internalDispatch({ type: 'address', value: order.deliveryAddress });
      internalDispatch({ type: 'email', value: order.email });
      // Initialize products from order
      const orderProducts =
        order.groups
          ?.filter(g => g.type === CustomerPageGroupType.Normal) // Normal groups only
          .flatMap(g => g.lines)
          .filter(line => line.isActive)
          .map(line => ({
            productId: line.productId,
            productName: line.name,
            productRecievedDate: '',
            quantity: 0, // Start with 0, user can select quantity
            unit: line.unit
          })) || [];
      internalDispatch({ type: 'products', value: orderProducts });
    } else {
      // For orderless forms, ensure products are empty
      internalDispatch({ type: 'products', value: [] });
    }
    if (error) {
      internalDispatch({
        type: 'hasError',
        value: true
      });
    }
  }, [error, order]);

  useEffect(() => {
    internalDispatch({ type: 'addressError', value: '' });
  }, [addressChange]);

  // Cleanup effect - reset state when component unmounts or order changes
  useEffect(() => {
    return () => {
      // Reset products when component unmounts to prevent state leakage
      internalDispatch({ type: 'products', value: [] });
      internalDispatch({ type: 'productsError', value: '' });
    };
  }, [order?.orderNumber]); // Reset when switching between different orders

  const completeInternal = () => {
    toggleLoading();
    internalDispatch({ type: 'generalError', value: '' });

    if (isValid(internalState, addressChange, internalDispatch)) {
      // If we have an order, use the existing order price match endpoint
      const basicQuestionsProps = {
        question: internalState.question,
        subject: internalState.subject,
        email: internalState.email,
        customerContactOption: internalState.contactOption
      };
      if (order && internalState.address && internalState.email) {
        return postSendExistingOrderPriceMatchQuestion({
          ...basicQuestionsProps,
          orderNumber: order.orderNumber,
          hash: order.hash,
          address: internalState.address,
          products: internalState.products.filter(p => p.quantity > 0)
        })
          .then(() => {
            onSuccess();
            scrollToTopIfNeeded();
            reduxDispatch(GtmCustomerSupportFormSubmit(FormType.PriceMatchExistingOrder));
          })
          .catch((response: IBasicResponse) => {
            internalDispatch({ type: 'generalError', value: response.message[0] });
          })
          .finally(() => {
            toggleLoading();
          });
      }

      // Otherwise, use the regular price match endpoint
      return postSendPriceMatchQuestion({
        ...basicQuestionsProps,
        name: internalState.name,
        phone: internalState.phone
      })
        .then(() => {
          onSuccess();
          scrollToTopIfNeeded();
          reduxDispatch(GtmCustomerSupportFormSubmit(FormType.PriceMatch));
        })
        .catch((response: IBasicResponse) => {
          internalDispatch({ type: 'generalError', value: response.message[0] });
        })
        .finally(() => {
          toggleLoading();
        });
    } else {
      toggleLoading();
      return Promise.resolve();
    }
  };

  return (
    <>
      {order && (
        <>
          <h3>{rm('customerpage_price_match_products_header')}</h3>
          <ExtendedInfo
            order={order}
            displayMode={DisplayMode.PriceMatch}
            className={styles.products}
            returnProductChanged={p => productChanged(p, internalDispatch)}
          />
          {internalState.productsError && (
            <BlueSectionContainer error={true}>{internalState.productsError}</BlueSectionContainer>
          )}
          <h3>{rm('customerpage_price_match_address_header')}</h3>
          <p className={styles.body}>{rm('customerpage_price_match_address_body')}</p>
          <BlueSectionContainer error={internalState.addressError}>
            {internalState.address ? (
              <Address
                address={internalState.address}
                email={internalState.email}
                onChange={(address, email) => addressChanged(address, email, internalDispatch)}
                changeMode={addressChange}
                toggleChange={toggleAddressChange}
                validationError={() => {
                  addressLookupValidationError(internalDispatch);
                }}
              />
            ) : (
              <Spinner className={styles.spinner} loading />
            )}
          </BlueSectionContainer>
        </>
      )}
      {!order && (
        <>
          <h3>{rm('Customerservice_Form_Product_Contact_Header')}</h3>
          <BlueSectionContainer error={internalState.addressError}>
            <div className={styles.contactColumn}>
              <div>
                <InputWithRules
                  change={e => internalDispatch({ type: 'name', value: e.target.value ?? '' })}
                  value={internalState.name}
                  type="text"
                  rule={nameRule}
                  placeholder={rm('Customerservice_Form_Product_Contact_Name')}
                />
              </div>

              <div>
                <InputWithRules
                  change={e => internalDispatch({ type: 'phone', value: e.target.value ?? '' })}
                  value={internalState.phone}
                  type="tel"
                  rule={phoneRule}
                  placeholder={rm('Customerservice_Form_Product_Contact_Phone')}
                  inputMode="tel"
                />
              </div>
            </div>

            <div>
              <InputWithRules
                change={e => internalDispatch({ type: 'email', value: e.target.value ?? '' })}
                value={internalState.email}
                type="email"
                rule={emailRule}
                placeholder={rm('Customerservice_Form_Product_Contact_Email')}
              />
            </div>
          </BlueSectionContainer>
        </>
      )}
      {internalState.addressError && <ErrorContainer>{internalState.addressError}</ErrorContainer>}
      <h3>
        {order
          ? rm('Customerservice_Form_Price_Match_Price_With_Order_Guarantee')
          : rm('Customerservice_Form_Price_Match_Price_Guarantee')}
      </h3>
      <BlueSectionContainer>
        <div className={styles.questionWrapper}>
          {!order && (
            <div>
              <strong>{rm('Customerservice_Form_Product_Question_Subject')}</strong>
              <Input
                onChange={(_event, newValue) =>
                  internalDispatch({ type: 'subject', value: newValue ?? '' })
                }
                value={internalState.subject}
              />
            </div>
          )}
          <div>
            <TextArea
              placeholder={rm('Customerservice_Form_Product_Question_Placeholder')}
              onChange={(_event, newValue) =>
                internalDispatch({ type: 'question', value: newValue ?? '' })
              }
              textlimit={QUESTION_MAX_LENGTH}
              value={internalState.question}
            />
            <p
              id={styles.returnMsgLength}
            >{`(${internalState.question.length}/${QUESTION_MAX_LENGTH})`}</p>
          </div>
        </div>
      </BlueSectionContainer>
      <CustomerContact
        contact={internalState.contactOption}
        onChange={val => contactOptionChanged(val, internalDispatch)}
        error={internalState.contactOptionError}
        addressAlreadyAdded={!order}
      />
      <SubmitContainer>
        <LoaderButton appearance="whitePillPrimary" onClick={completeInternal} loading={loading}>
          {loading ? '' : rm('customerpage_returns_submit')}
        </LoaderButton>
        <Button appearance="whitePill" href={returnUrl}>
          {rm('customerpage_returns_cancel')}
        </Button>
      </SubmitContainer>
      {internalState.generalError && (
        <BlueSectionContainer error>{internalState.generalError}</BlueSectionContainer>
      )}
    </>
  );
};
